import React from 'react';

import { Box } from '@components/Restyle';
import { Skeleton } from '@components/shared/Skeleton';
import { MemberRowSkeleton, SkeletonList } from '@components/shared/skeletons';
import theme from '@themes/theme';

/** `TaskForm`'s TextField inputs, project card and date/priority chips. */
const FIELD_HEIGHT = 48;
const CHIP_HEIGHT = 32;

const renderMemberRowSkeleton = (index: number) => (
  <Box key={index} marginBottom='s'>
    <MemberRowSkeleton />
  </Box>
);

const LabelledField: React.FC<{ labelWidth: number; height?: number }> = ({
  labelWidth,
  height = FIELD_HEIGHT,
}) => (
  <Box marginTop='l'>
    <Skeleton variant='text' width={labelWidth} />
    <Box marginTop='xs'>
      <Skeleton variant='rect' width='100%' height={height} borderRadius='xs' />
    </Box>
  </Box>
);

/**
 * Placeholder for the native Edit Task form (TA-10389), mirroring
 * `TaskForm.tsx`: name, description, the project card, the start/due date
 * chips, the priority chips, then the members section's rows.
 *
 * `EditTask.tsx` can't mount `TaskFormStack` before `getTask` resolves —
 * Formik's `initialValues` are derived from the task — so this stands in for
 * the whole form rather than for individual fields, replacing a bare centred
 * spinner.
 */
export const TaskFormSkeleton: React.FC<{ testID?: string }> = ({
  testID = 'task-form-skeleton',
}) => {
  return (
    <Box testID={testID} flex={1} paddingHorizontal='m' marginTop='l'>
      <LabelledField labelWidth={45} />
      <LabelledField labelWidth={75} height={80} />
      <LabelledField labelWidth={50} height={64} />

      <Box marginTop='l'>
        <Skeleton variant='text' width={80} />
        <Box marginTop='xs' flexDirection='row' justifyContent='space-between'>
          <Box flex={1} marginRight='s'>
            <Skeleton
              variant='rect'
              width='100%'
              height={CHIP_HEIGHT}
              borderRadius='xs'
            />
          </Box>
          <Box flex={1} marginLeft='s'>
            <Skeleton
              variant='rect'
              width='100%'
              height={CHIP_HEIGHT}
              borderRadius='xs'
            />
          </Box>
        </Box>
      </Box>

      <Box marginTop='l'>
        <Skeleton variant='text' width={60} />
        <Box
          marginTop='xs'
          flexDirection='row'
          style={{ gap: theme.spacing.xxs }}>
          {[0, 1, 2].map((index) => (
            <Skeleton
              key={index}
              variant='rect'
              width={80}
              height={CHIP_HEIGHT}
              borderRadius='xs'
            />
          ))}
        </Box>
      </Box>

      <Box marginTop='l' marginBottom='xl'>
        <Skeleton variant='text' width={90} />
        <Box marginTop='s'>
          <SkeletonList row={renderMemberRowSkeleton} />
        </Box>
      </Box>
    </Box>
  );
};
